import React from 'react';
import { RotateCw } from 'lucide-react';
import type {
  ImageAdjustInteractionKind,
  ImageTransformHandleKind,
} from './imageBlockEditing';

type ImageTransformHandlesProps = {
  interactionKind?: ImageAdjustInteractionKind | null;
  onHandlePointerDown: (
    event: React.PointerEvent<HTMLButtonElement>,
    handleKind: ImageTransformHandleKind,
    bounds: DOMRect | null
  ) => void;
  onHandlePointerMove: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onHandlePointerUp: (event: React.PointerEvent<HTMLButtonElement>) => void;
};

const CORNER_HANDLES: Array<{
  kind: Extract<
    ImageTransformHandleKind,
    'corner-nw' | 'corner-ne' | 'corner-se' | 'corner-sw'
  >;
  className: string;
  cursor: string;
}> = [
  { kind: 'corner-nw', className: 'left-0 top-0 -translate-x-1/2 -translate-y-1/2', cursor: 'nwse-resize' },
  { kind: 'corner-ne', className: 'right-0 top-0 translate-x-1/2 -translate-y-1/2', cursor: 'nesw-resize' },
  { kind: 'corner-se', className: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2', cursor: 'nwse-resize' },
  { kind: 'corner-sw', className: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2', cursor: 'nesw-resize' },
];

const SIDE_HANDLES: Array<{
  kind: Extract<ImageTransformHandleKind, 'side-n' | 'side-e' | 'side-s' | 'side-w'>;
  className: string;
  cursor: string;
}> = [
  { kind: 'side-n', className: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2', cursor: 'ns-resize' },
  { kind: 'side-e', className: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2', cursor: 'ew-resize' },
  { kind: 'side-s', className: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2', cursor: 'ns-resize' },
  { kind: 'side-w', className: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2', cursor: 'ew-resize' },
];

const ROTATE_HANDLES: Array<{
  kind: Extract<
    ImageTransformHandleKind,
    'rotate-nw' | 'rotate-ne' | 'rotate-se' | 'rotate-sw'
  >;
  className: string;
}> = [
  { kind: 'rotate-nw', className: 'left-0 top-0 -translate-x-[140%] -translate-y-[140%]' },
  { kind: 'rotate-ne', className: 'right-0 top-0 translate-x-[140%] -translate-y-[140%]' },
  { kind: 'rotate-se', className: 'bottom-0 right-0 translate-x-[140%] translate-y-[140%]' },
  { kind: 'rotate-sw', className: 'bottom-0 left-0 -translate-x-[140%] translate-y-[140%]' },
];

const BASE_HANDLE_CLASS =
  'pointer-events-auto absolute z-[34] inline-flex items-center justify-center rounded-full border border-sky-500 bg-white shadow-[0_2px_10px_rgba(15,23,42,0.18)] transition';

const ROTATE_CURSOR =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7a5 5 0 1 1-5 5H5a7 7 0 1 0 12.65-5.65Z' fill='%230ea5e9'/%3E%3C/svg%3E\") 12 12, crosshair";

const ImageTransformHandles: React.FC<ImageTransformHandlesProps> = ({
  interactionKind,
  onHandlePointerDown,
  onHandlePointerMove,
  onHandlePointerUp,
}) => {
  const boundsRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <div ref={boundsRef} className="pointer-events-none absolute inset-[12px] z-[33]">
      {CORNER_HANDLES.map((handle) => (
        <button
          key={handle.kind}
          type="button"
          aria-label="Scale image uniformly"
          title="Scale image uniformly"
          className={`${BASE_HANDLE_CLASS} h-3.5 w-3.5 ${handle.className} ${
            interactionKind === 'scale-uniform' ? 'ring-2 ring-sky-300' : ''
          }`}
          style={{ cursor: handle.cursor }}
          data-builder-allow-interaction
          onPointerDown={(event) =>
            onHandlePointerDown(event, handle.kind, boundsRef.current?.getBoundingClientRect() ?? null)
          }
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
        />
      ))}
      {SIDE_HANDLES.map((handle) => (
        <button
          key={handle.kind}
          type="button"
          aria-label="Stretch image"
          title="Stretch image"
          className={`${BASE_HANDLE_CLASS} ${
            handle.kind === 'side-n' || handle.kind === 'side-s' ? 'h-3 w-8 rounded-full' : 'h-8 w-3 rounded-full'
          } ${handle.className} ${
            interactionKind === 'scale-x' || interactionKind === 'scale-y' ? 'ring-2 ring-sky-300' : ''
          }`}
          style={{ cursor: handle.cursor }}
          data-builder-allow-interaction
          onPointerDown={(event) =>
            onHandlePointerDown(event, handle.kind, boundsRef.current?.getBoundingClientRect() ?? null)
          }
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
        />
      ))}
      {ROTATE_HANDLES.map((handle) => (
        <button
          key={handle.kind}
          type="button"
          aria-label="Rotate image"
          title="Rotate image"
          className={`${BASE_HANDLE_CLASS} h-6 w-6 border-sky-400 bg-white text-sky-700 shadow-[0_3px_12px_rgba(15,23,42,0.22)] ${handle.className} ${
            interactionKind === 'rotate' ? 'ring-2 ring-sky-300' : ''
          }`}
          style={{ cursor: ROTATE_CURSOR }}
          data-builder-allow-interaction
          onPointerDown={(event) =>
            onHandlePointerDown(event, handle.kind, boundsRef.current?.getBoundingClientRect() ?? null)
          }
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
        >
          <RotateCw className="h-3.5 w-3.5" strokeWidth={2.2} />
        </button>
      ))}
    </div>
  );
};

export default ImageTransformHandles;
